// src/hooks/useFeatureGrants.ts — which allow-listed features this organisation holds.
//
// One fetch of GET /v1/feature-grants/mine per organisation, shared by the
// sidebar, the mobile nav and PrivateRoute (same reasoning as useFeatureAccess).
//
// Returns undefined while unknown (loading, or the call failed). navVisibility
// treats that differently for links and for routes — see grantAllows there.

import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { featureGrantsService } from 'api/services/featureGrants.service';

type Entry = { grants?: string[]; inflight?: Promise<void> };

const cache = new Map<string, Entry>();

/** Exported for tests, and so a master's grant/revoke is reflected without a reload. */
export function clearFeatureGrantsCache(): void {
  cache.clear();
}

export function useFeatureGrants(): string[] | undefined {
  const { user, currentOrganization } = useAuth();
  const orgId = (currentOrganization as any)?.id ?? (user as any)?.organization_id ?? null;

  const [grants, setGrants] = useState<string[] | undefined>(orgId ? cache.get(orgId)?.grants : undefined);

  useEffect(() => {
    if (!orgId || !user) {
      setGrants(undefined);
      return;
    }

    let alive = true;
    const entry = cache.get(orgId) ?? {};
    cache.set(orgId, entry);

    if (entry.grants) {
      setGrants(entry.grants);
      return;
    }

    if (!entry.inflight) {
      entry.inflight = featureGrantsService.mine().then(res => {
        if (res.success && Array.isArray(res.data?.grants)) {
          entry.grants = res.data!.grants;
        }
        entry.inflight = undefined;
      }).catch(() => {
        entry.inflight = undefined;
      });
    }

    entry.inflight.then(() => {
      if (alive) setGrants(cache.get(orgId)?.grants);
    });

    return () => { alive = false; };
  }, [orgId, user]);

  return grants;
}
